import type { CSSProperties } from 'react';
import React from 'react';
import type { ChildrenProp, ClassNameProp } from '../common-types';
import { isEmptyString } from '../../util';

/**
 * Type used for all the flex breakpoints.
 */
type flexGridBreakpointType =
  | boolean
  | 'auto'
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12;

/**
 * Props for a {@link Grid} component.
 */
export interface GridProps extends Required<ChildrenProp>, ClassNameProp {
  /**
   * If `true`, the component will have the flex *container* behavior.
   * You should be wrapping *items* with a *container*.
   */
  container?: boolean;
  /**
   * If `true`, the component will have the flex *item* behavior.
   * You should be wrapping *items* with a *container*.
   */
  item?: boolean;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `lg` breakpoint and wider screens if not overridden.
   */
  lg?: flexGridBreakpointType;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `md` breakpoint and wider screens if not overridden.
   */
  md?: flexGridBreakpointType;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `sm` breakpoint and wider screens if not overridden.
   */
  sm?: flexGridBreakpointType;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `xl` breakpoint and wider screens if not overridden.
   */
  xl?: flexGridBreakpointType;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `xs` breakpoint and wider screens if not overridden.
   */
  xs?: flexGridBreakpointType;
  /**
   * Wrapping behaviour.
   * See {@link https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap} for details.
   */
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  /**
   * Element spacing.
   */
  spacing?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  /**
   * Flex direction.
   */
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  /**
   * Inline CSS to add to the element.
   * Properties that are manipulated through other properties are omitted here.
   */
  style?: Omit<CSSProperties, 'flexDirection' | 'flexWrap'>;
}

// overloads to restrict force at least 'item' or 'container' to be set
export function Grid(props: GridProps & { item: true }): React.JSX.Element;
export function Grid(props: GridProps & { container: true }): React.JSX.Element;
/**
 * Layouting component for creating a flexbox grid layout.
 * This component is used both as a Grid container and an item depending on the passed props.
 *
 * @param properties a {@link GridProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function Grid({
  className = '',
  style,
  wrap,
  direction,
  children,
  item,
  xl,
  xs,
  lg,
  sm,
  md,
  spacing,
  container,
  ...props
}: Readonly<GridProps>): React.JSX.Element {
  // collect the full css class list of the component depending on the props
  const elementClasses = ['ehs-grid'];
  if (xs) elementClasses.push(`grid-xs-${xs}`);
  if (xl) elementClasses.push(`grid-xl-${xl}`);
  if (sm) elementClasses.push(`grid-sm-${sm}`);
  if (md) elementClasses.push(`grid-md-${md}`);
  if (lg) elementClasses.push(`grid-lg-${lg}`);
  if (item) elementClasses.push('item');
  if (container) elementClasses.push('container');
  if (spacing) elementClasses.push(`spacing-${spacing}`);
  // custom passed classes
  if (!isEmptyString(className)) elementClasses.push(className);

  return (
    <div
      {...props}
      className={elementClasses.join(' ')}
      style={{ flexDirection: direction, flexWrap: wrap, ...style }}
    >
      {children}
    </div>
  );
}
